import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../Helpers/Handler";
import { formatDate, textCapitalize } from "../Helpers/Utils";

// Shared by the leave screens: how a leave is worded (with its half), which days of it can still be cancelled,
// and the dialog that withdraws a pending leave, asks to cancel an approved one, or (for a supervisor / admin) cancels it.
// Kept identical in the Staff and Admin web apps.

export const HALF_LABEL = { FIRST_HALF: "First half", SECOND_HALF: "Second half" };
export const LEAVE_TYPE_LABEL = { CASUAL_LEAVE: "Casual leave", SICK_LEAVE: "Sick leave", EMERGENCY_LEAVE: "Emergency leave", HALF_DAY: "Half day" };
export const LEAVE_STATUS_CLASS = { PENDING: "att-warn", APPROVED: "att-ok", REJECTED: "att-bad", CANCELLED: "att-muted" };

// "Half day (first half)" - a half day saved by an older app has no half and reads just "Half day".
export const leaveTypeText = (row) => {
    const base = LEAVE_TYPE_LABEL[row?.type] || textCapitalize(String(row?.type || "").replace(/_/g, " "));
    return row?.type === "HALF_DAY" && HALF_LABEL[row?.session] ? `${base} (${HALF_LABEL[row.session].toLowerCase()})` : base;
};

const pad = (value) => String(value).padStart(2, "0");
// Today on the person's own calendar, as YYYY-MM-DD.
export const todayIso = () => {
    const now = new Date();
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};
// Leave dates are stored as midnight UTC, so the UTC date part is the calendar day.
export const dayOf = (value) => new Date(value).toISOString().slice(0, 10);

// The days of this leave that can still be cancelled - today and later, inside the leave - or null when none.
export const cancellableDays = (row) => {
    if (!row?.fromDate || !row?.toDate) return null;
    const today = todayIso();
    const to = dayOf(row.toDate);
    if (to < today) return null;
    const from = dayOf(row.fromDate);
    return { from: from > today ? from : today, to };
};

export const hasWaitingCancelRequest = (row) => row?.cancelRequest?.status === "PENDING";

// A small chip for the staff member's own list: "Cancel requested" / "Cancel request rejected".
export const CancelRequestChip = ({ row }) => {
    const state = row?.cancelRequest?.status;
    if (state === "PENDING") return <span className="att-chip att-warn ms-1">Cancel requested</span>;
    if (state === "REJECTED" && row?.status === "APPROVED") return <span className="att-chip att-bad ms-1">Cancel request rejected</span>;
    return null;
};

const MODES = {
    withdraw: { title: "Withdraw leave request", button: "Withdraw request", note: "This request has not been decided yet, so it is withdrawn straight away.", needsReason: false, done: "Leave withdrawn" },
    request: { title: "Request to cancel leave", button: "Send cancel request", note: "Your supervisor will decide this. Until they approve it your leave stays as it is, and you cannot check in on those days.", needsReason: true, done: "Cancel request sent" },
    direct: { title: "Cancel leave", button: "Cancel leave", note: "The days you choose are cancelled at once and the staff member is told, so they can check in on those days.", needsReason: true, done: "Leave cancelled" },
};

// leave: the row; mode: "withdraw" | "request" | "direct"; url: the endpoint to POST to.
export const LeaveCancelModal = ({ leave, mode, url, onClose, onDone }) => {
    const modalRef = useRef(null);
    const config = MODES[mode] || MODES.request;
    const days = cancellableDays(leave) || { from: dayOf(leave.fromDate), to: dayOf(leave.toDate) };
    const single = leave.type === "HALF_DAY" || days.from === days.to;
    const [from, setFrom] = useState(days.from);
    const [to, setTo] = useState(days.to);
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async () => {
        if (config.needsReason && !reason.trim()) { toast.error("Please give a reason"); return; }
        if (config.needsReason && !single) {
            if (!from || !to) { toast.error("Choose the days to cancel"); return; }
            if (to < from) { toast.error("The end date cannot be before the start date"); return; }
        }
        setSaving(true);
        const response = await Handler({
            method: "post",
            url,
            data: { ...(config.needsReason ? { fromDate: single ? days.from : from, toDate: single ? days.to : to } : {}), reason: reason.trim() },
        });
        if (response?.success && response?.data?.success) {
            toast.success(response?.data?.message || config.done);
            onDone?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to cancel the leave"));
        }
        setSaving(false);
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">{config.title}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <p className="mb-2">
                            <span className="fw-semibold">{leaveTypeText(leave)}</span>
                            {" · "}{formatDate(leave.fromDate)}{dayOf(leave.fromDate) !== dayOf(leave.toDate) ? ` to ${formatDate(leave.toDate)}` : ""}
                        </p>
                        <div className="att-inline-note mb-3">{config.note}</div>
                        {config.needsReason && !single ? (
                            <div className="row g-3 mb-3">
                                <div className="col-6">
                                    <label className="form-label" htmlFor="cancel-from">Cancel from</label>
                                    <input id="cancel-from" type="date" className="form-control" min={days.from} max={days.to} value={from} onChange={(event) => setFrom(event.target.value)} />
                                </div>
                                <div className="col-6">
                                    <label className="form-label" htmlFor="cancel-to">Cancel until</label>
                                    <input id="cancel-to" type="date" className="form-control" min={days.from} max={days.to} value={to} onChange={(event) => setTo(event.target.value)} />
                                </div>
                                <div className="col-12 form-text mt-1">Only today and later days can be cancelled. The other days of this leave stay.</div>
                            </div>
                        ) : null}
                        {config.needsReason ? (
                            <div>
                                <label className="form-label" htmlFor="cancel-reason">Reason</label>
                                <textarea id="cancel-reason" className="form-control" rows={2} maxLength={500} placeholder="Why is this leave being cancelled?" value={reason} onChange={(event) => setReason(event.target.value)} />
                            </div>
                        ) : null}
                    </div>
                    <div className="modal-footer border-0">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal" disabled={saving}>Keep leave</button>
                        <button type="button" className="btn btn-danger" disabled={saving} onClick={submit}>{saving ? "Please wait..." : config.button}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};
